import React from 'react';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { useTheme } from '@mui/material/styles';
import { useParams } from 'react-router-dom';
import {
	Box,
	CardContent,
	Checkbox,
	Stack,
	Typography,
	CircularProgress,
	Tooltip,
	TableContainer,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Button
} from '@mui/material';
import NumericStepper from 'components/NumericStepper';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import DeleteIcon from '@mui/icons-material/Delete';
import CakeIcon from '@mui/icons-material/Cake';
import Card from 'components7/Card';
import utils from 'components/utils';

const DetailItems = props => {
	const { proposal } = props;
	const theme = useTheme();
	const translate = utils.useI18N();
	const loading = useStoreState(state => state.kit.detail.loading);
	const kit = useStoreState(state => state.kit.detail.data);
	const selectAll = useStoreActions(dispatch => dispatch.kit.detail.selectAll);
	const deselectAll = useStoreActions(dispatch => dispatch.kit.detail.deselectAll);
	const toggleSelection = useStoreActions(dispatch => dispatch.kit.detail.toggleSelection);
	const setQuantityItem = useStoreActions(dispatch => dispatch.kit.detail.setQuantityItem);
	const deleteItems = useStoreActions(dispatch => dispatch.kit.detail.deleteItems);
	const updateItems = useStoreActions(dispatch => dispatch.kit.detail.updateItems);
	const numItems = kit.kitItems ? kit.kitItems.length : 0;
	const itemSelected = kit.kitItems ? kit.kitItems.filter(i => i.isSelected) : [];
	const itemUpdated = kit.kitItems
		? kit.kitItems.filter(i => i.quantity !== i.quantityOriginal && i.quantity !== 0)
		: [];

	const headCells = [
		{ id: 'quantity', label: 'common.quantity', width: '80px', sortable: false },
		{ id: 'ean', label: 'kit.table.ean', width: '125px', sortable: true },
		{ id: 'gadget', label: '', width: '20px', sortable: false },
		{ id: 'title', label: 'kit.table.title', width: 'auto', sortable: true }
	];

	const onSelectAll = event => {
		if (event.target.checked) selectAll();
		else deselectAll();
	};

	return (
		<Card backgroundColor="#fff" title={translate('kit.items') + ' (' + numItems + ')'} width="100%">
			<CardContent sx={{ paddingBottom: '0' }}>
				{loading && (
					<Box sx={{ width: '100%', textAlign: 'center' }}>
						<CircularProgress size={32} sx={{ color: theme.palette.primary.main }} />
					</Box>
				)}
				{numItems == 0 && (
					<Box sx={{ width: '100%', textAlign: 'center' }}>
						<Typography variant="textTab" sx={{ fontWeight: '600' }}>
							{translate('kit.noKitItems')}
						</Typography>
					</Box>
				)}
				{numItems > 0 && (
					<Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={0}>
						<Stack
							direction="row"
							justifyContent="flex-end"
							alignItems="center"
							spacing={2}
							sx={{ width: '100%', marginTop: '-15px !important' }}
						>
							<Button
								variant="dark"
								disabled={itemUpdated.length === 0}
								endIcon={<PlaylistAddCheckIcon sx={{ color: theme.palette.custom.white }} />}
								onClick={updateItems}
							>
								{translate('kit.updateQuanitity')}
							</Button>
							<Button
								variant="dark"
								disabled={itemSelected.length === 0}
								endIcon={<DeleteIcon sx={{ color: theme.palette.custom.white }} />}
								onClick={deleteItems}
							>
								{translate('common.delete')}
							</Button>
						</Stack>
						<TableContainer sx={{ overflowY: 'scroll', height: '438px', marginTop: '20px !important' }}>
							<Table aria-labelledby="items" aria-label="items table" padding="none">
								<TableHead>
									<TableRow>
										<TableCell
											sx={{
												background: theme.palette.custom.greytable,
												fontWeight: '600',
												width: '54px'
											}}
										>
											<Checkbox
												indeterminate={
													itemSelected.length > 0 && itemSelected.length < kit.kitItems.length
												}
												checked={
													itemSelected.length > 0 &&
													kit.kitItems.length === itemSelected.length
												}
												onChange={onSelectAll}
												inputProps={{ 'aria-label': 'select all items' }}
											/>
										</TableCell>
										{headCells.map(headCell => (
											<TableCell
												key={headCell.id}
												align="left"
												sx={{
													background: theme.palette.custom.greytable,
													fontWeight: '600',
													width: headCell.width
												}}
											>
												{translate(headCell.label)}
											</TableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{kit.kitItems.map((row, index) => (
										<TableRow key={index}>
											<TableCell align="left">
												<Checkbox
													checked={row.isSelected}
													onChange={e => toggleSelection(row.id)}
													inputProps={{
														'aria-labelledby': row.id
													}}
												/>
											</TableCell>
											<TableCell sx={{ paddingRight: '20px' }}>
												<NumericStepper
													removeDot
													value={row.quantity}
													max={999}
													precision={0}
													min={0}
													strict
													onChange={numberAsValue =>
														setQuantityItem({ id: row.id, number: numberAsValue })
													}
												/>
											</TableCell>
											<TableCell>
												<Typography variant="textTable">{row.ean}</Typography>
											</TableCell>
											<TableCell align="center" sx={{ paddingRight: '5px' }}>
												{row.catalog.type == 9 && (
													<Tooltip title={translate('common.gadget')}>
														<CakeIcon sx={{ color: theme.palette.primary.main }} />
													</Tooltip>
												)}
											</TableCell>
											<TableCell>
												<Typography variant="textTable">{row.catalog.title}</Typography>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</Stack>
				)}
			</CardContent>
		</Card>
	);
};

export default DetailItems;
